/**
 * The impersonation-banner → gateway-shell height protocol (#3950).
 *
 * When the framed Studio SPA shows its amber tenant-impersonation banner, it
 * postMessages its measured height up to the parent `GatewayShell` so the shell
 * can push its launcher orb (painted on top of the iframe) down out of the
 * banner's y-range. This module is the SINGLE source of truth for that wire
 * contract: the emitter (`TenantImpersonationBanner`) and the listener
 * (`GatewayShell`) both build/parse messages through the helpers below, so
 * reshaping the message breaks the other side at compile time instead of
 * silently dropping messages at runtime.
 *
 * The origin check is deliberately NOT here — it depends on
 * `window.location.origin`, which only the listener (the top window) can
 * evaluate. Everything about the payload shape lives here.
 */

/** Identifies the sender as the framed Studio SPA (distinct from the shell's own bridge). */
export const BANNER_MESSAGE_SOURCE = 'matrix-spa';

/** The message type for the banner-height handshake. */
export const BANNER_MESSAGE_TYPE = 'impersonation-banner';

/** The wire shape of a banner-height message. */
export type BannerHeightMessage = {
  source: string;
  type: string;
  height: number;
};

/** Builds a banner-height message for the emitter to post to the parent shell. */
export function makeBannerHeightMessage(height: number): BannerHeightMessage {
  return { source: BANNER_MESSAGE_SOURCE, type: BANNER_MESSAGE_TYPE, height };
}

/**
 * Coerces unknown `postMessage` data into a normalized banner height.
 *
 * Returns `null` when the payload is NOT a well-formed banner-height message
 * (a non-object payload, or a wrong `source`/`type`) so the listener ignores
 * it. Otherwise returns a non-negative integer: the rounded `height` when it is
 * a positive number, and `0` when it is absent/non-numeric or already `<= 0`
 * (a `0` is the emitter's "the banner is gone — rebound the orb" signal).
 *
 * Centralizing the shape check here keeps the listener and its tests reading
 * the message the same way — the "lockstep" the contract claims is enforced by
 * code, not by a comment.
 *
 * `data` is typed `unknown` (not `any`) on purpose: `postMessage` payloads
 * cross a trust boundary — a buggy or malicious frame can post ANY value — so
 * the parser narrows from `unknown` and checks every field before use. This
 * is the deliberate cross-frame defense (review nit A1) and is meant to stay
 * `unknown`, never `any`.
 */
export function parseBannerHeightMessage(data: unknown): number | null {
  if (typeof data !== 'object' || data === null) return null;
  const d = data as { source?: unknown; type?: unknown; height?: unknown };
  if (d.source !== BANNER_MESSAGE_SOURCE || d.type !== BANNER_MESSAGE_TYPE) return null;
  return typeof d.height === 'number' && d.height > 0 ? Math.round(d.height) : 0;
}

/**
 * The `sessionStorage` key the gateway shell caches the last-known banner
 * height under. Session storage is tab-scoped, matching the impersonation
 * session's lifetime (closing the tab resets the guess). The SPA and the shell
 * are same-origin in the same tab, so they SHARE this storage: the shell writes
 * it (on a height post) and the SPA clears it (on Exit to Parent), so the
 * freshly-reloaded shell seeds the orb at the default inset instead of
 * reopening stuck below a banner that no longer exists. Exported here so both
 * sides reference the SAME key rather than a duplicated literal.
 */
export const BANNER_OFFSET_STORAGE_KEY = 'matrix:gateway-shell:impersonation-banner-height';

/**
 * Clears the shell's cached banner offset so the next shell load seeds at the
 * default inset. The SPA calls this on Exit to Parent, BEFORE the top window
 * navigates (`target="_top"`), so the reloaded shell does not reopen with the
 * orb stuck below a banner that no longer exists. Robust to storage being
 * blocked (a blocked `removeItem` is a no-op, same as the shell's writer).
 */
export function clearCachedBannerOffset(): void {
  try {
    window.sessionStorage.removeItem(BANNER_OFFSET_STORAGE_KEY);
  } catch {
    /* storage blocked */
  }
}
